import { Injectable, OnDestroy, inject } from '@angular/core';
import { BehaviorSubject, first, firstValueFrom, Observable } from 'rxjs';
import { TokenStoreService } from './token-store.service';
import {
  AuthToken,
  BanShortResponse,
  Body,
  Body2,
  Client,
  FileResponse,
  LangResponse,
  LoginSmsRequest,
  Message,
  ReplyOnResolveDispute,
  SendSmsRequest,
  SkillsRequest,
  SubscriptionsInfo,
  UserDataResponse,
  UserResponse,
  VisitDataRequest,
} from '@api/client';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from './toast.service';
import { DatePipe } from '@angular/common';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { LocalStorageService } from '@services/local-storage.service';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { LoadingService } from './loading.service';
import { OnlineStateService } from './online-state.service';
import { SubscriptionsBag } from '@utils/subscriptions-bag';
import { JwtService } from './jwt.service';
import { AppInfoService } from './app-info.service';
import { PhoneInfoService } from '@avatar/services/phone-info.service';
import { WindowService } from './window.service';
import { HttpStatusCode } from '@angular/common/http';
import { environment } from '@environments/environment';
import { map } from 'rxjs/operators';

interface AppleProfile {
  user: string;
  email: string | null;
  givenName: string | null;
  familyName: string | null;
}

interface GoogleProfile {
  email: string | null;
  familyName: string | null;
  givenName: string | null;
  id: string | null;
  name: string | null;
  imageUrl: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {
  private readonly client = inject(Client);
  private readonly tokenStore = inject(TokenStoreService);
  private readonly translate = inject(TranslateService);
  private readonly datePipe = inject(DatePipe);
  private readonly toastService = inject(ToastService);
  private readonly loadingService = inject(LoadingService);
  private readonly onlineStateService = inject(OnlineStateService);
  private readonly jwtService = inject(JwtService);
  private readonly appInfoService = inject(AppInfoService);
  private readonly phoneInfoService = inject(PhoneInfoService);
  private readonly window = inject(WindowService);
  private readonly localStorage = inject(LocalStorageService);

  public appleUser: AppleProfile;
  public googleUser: GoogleProfile;
  // private readonly JWT_TOKEN: string = 'authToken';
  // private readonly USER_INFO: string = 'userInfo';
  private refreshTokenMutex: Promise<void> = null;
  private parsedTokenSubj: BehaviorSubject<ParsedToken | null> = new BehaviorSubject(null);
  private parsedUserSubj: BehaviorSubject<ParsedUser | null> = new BehaviorSubject(null);
  private sb: SubscriptionsBag = new SubscriptionsBag();
  private userDataLoadPromise: Promise<void> | null = null;
  public isAuthenticated$: Observable<boolean> = this.parsedTokenSubj.asObservable().pipe(
    map((parsedToken: ParsedToken | null): boolean => !!parsedToken),
  );

  ngOnDestroy() {
    this.sb.unsubscribeAll();
  }

  public get parsedToken$(): Observable<ParsedToken | null> {
    return this.parsedTokenSubj.asObservable();
  }

  public get parsedToken(): ParsedToken | null {
    return this.parsedTokenSubj.value;
  }

  public get parsedUser$(): Observable<ParsedUser | null> {
    return this.parsedUserSubj.asObservable();
  }

  public get parsedUser(): ParsedUser | null {
    return this.parsedUserSubj.value;
  }

  public get isAuthenticated(): boolean {
    return !!this.parsedToken;
  }

  public get isUserProfileIncomplete(): boolean {
    const parsedUser: ParsedUser = this.parsedUser;
    return parsedUser && (!parsedUser?.name || !parsedUser?.surname);
  }

  private set parsedToken(parsedToken: ParsedToken | null) {
    this.parsedTokenSubj.next(parsedToken);
  }

  private set parsedUser(parsedUser: ParsedUser | null) {
    this.parsedUserSubj.next(parsedUser);
  }

  public async init(): Promise<void> {
    if (this.onlineStateService.isOffline) {
      this.sb.sub = this.onlineStateService.isOffline$.pipe(
        first((isOffline: boolean): boolean => !isOffline),
      ).subscribe(async (): Promise<void> => {
        await this.init();
      });
      return;
    }

    try {
      const jwtToken: string = await this.tokenStore.getToken();
      this.parsedToken = ParsedToken.fromToken(jwtToken);

      void this.sendVisitData().catch((error: unknown): void => {
        console.error('sendVisitData error', error);
      });

      if (!this.parsedToken) return;

      if (!this.parsedToken?.userId) {
        console.error('Invalid token!');
        this.parsedUser = null;
        return;
      }

      void this.loadUserData(this.parsedToken.userId);
    } catch (error) {
      console.error(error);
    }
  }

  private loadUserData(userId: number): Promise<void> {
    if (this.userDataLoadPromise) return this.userDataLoadPromise;

    this.userDataLoadPromise = firstValueFrom(this.client.userGet(String(userId)))
      .then((userDataResponse: UserDataResponse): void => {
        this.setParsedUser(userDataResponse);
      })
      .catch((error: unknown): void => {
        console.error('loadUserData error', error);
      })
      .finally((): void => {
        this.userDataLoadPromise = null;
      });

    return this.userDataLoadPromise;
  }

  public setParsedUser(userDataResponse: UserDataResponse): void {
    this.parsedUser = ParsedUser.fromResponse(userDataResponse?.data);
  }

  private async sendVisitData(): Promise<void> {
    const body: VisitDataRequest = new VisitDataRequest({
      device: await this.phoneInfoService.getDeviceModelWithOS(),
      app_version: await this.appInfoService.getShortAppVersion(),
    });

    if (!this.parsedToken) {
      body.temp_token = this.tokenStore.getTempToken();
      body.resolution = this.window.getScreenResolution();
    }
    await firstValueFrom(this.client.userVisitData(body));
  }

  public async loginWithToken(request: Body): Promise<void> {
    try {
      const authToken: AuthToken = await firstValueFrom(this.client.authLogin(request));

      if (authToken?.user?.role.includes('user') || authToken?.user?.role.includes('admin')) {
        await this.loginUserIfTokenAvailable(authToken);
      }
    } catch (error) {
      await this.authExecutionHandler(error);
    }
  }

  public async loginWithSmsCode(request: LoginSmsRequest): Promise<void> {
    try {
      await this.loadingService.start();

      const authToken: AuthToken = await firstValueFrom(this.client.authLoginSms(request));
      if (authToken?.user?.role.includes('user') || authToken?.user?.role.includes('admin')) {
        await this.loginUserIfTokenAvailable(authToken);
      } else {
        console.error('Invalid user role:', authToken?.user?.role);
      }
    } catch (error) {
      await this.authExecutionHandler(error);
      throw error;
    } finally {
      await this.loadingService.stop();
    }
  }

  public async sendSmsCodeRequest(request: SendSmsRequest): Promise<string> {
    try {
      const response: Message = await firstValueFrom(this.client.authSendSms(request));
      return response?.message;
    } catch (error) {
      if (error?.status === HttpStatusCode.TooManyRequests) {
        throw error;
      }

      console.error('Send SMS code request error:', error);
      await this.authExecutionHandler(error);
    }
  }

  private async authExecutionHandler(error: any): Promise<void> {
    const responseMessage: string = error?.result?.error ?? error?.result?.message;

    if (responseMessage === 'auth.user_not_found') {
      await this.toastService.error(this.translate.instant(responseMessage));
    } else if (responseMessage === 'auth.sms_service_not_available_for_your_mobile_operator') {
      await this.toastService.error(this.translate.instant(responseMessage));
    } else if (responseMessage === 'auth.invalid_code') {
      // processing in function smsCodeChanged
    } else {
      // await this.httpErrorHandlerService.handleHttpException(ex);
      const parsedException = JSON.parse(error?.response);
      const message = parsedException?.message ?? parsedException?.error;
      console.error('Login error:', error);
      await this.toastService.error(this.translate.instant(message));
    }
    throw error;
  }

  public async authMe(): Promise<UserDataResponse> {
    try {
      return await firstValueFrom(this.client.authMe());
    } catch (error) {
      console.error(error);
    }
  }

  public async refreshToken(): Promise<void> {
    if (this.refreshTokenMutex) {
      return this.refreshTokenMutex;
    }

    this.refreshTokenMutex = new Promise<void>(async (resolve, reject): Promise<void> => {
      const refreshToken: string = await this.tokenStore.getRefreshToken();

      if (!refreshToken) {
        console.error('No refresh token, logout...');
        this.refreshTokenMutex = null;
        await this.toastService.warning(this.translate.instant('auth.unauthorized'));
        await this.logout();
        return reject({message: 'No refresh token!'});
      }

      const isRefreshTokenExpired: boolean = this.jwtService.checkIsTokenExpired(refreshToken);

      if (isRefreshTokenExpired) {
        console.error('Refresh token expired, logout...');
        this.refreshTokenMutex = null;
        await this.toastService.warning(this.translate.instant('auth.unauthorized'));
        await this.logout();
        return reject({message: 'Refresh token expired!'});
      }

      try {
        const body: Body2 = new Body2({refresh_token: refreshToken});
        console.warn('Try to refresh token...');
        const authToken: AuthToken = await firstValueFrom(this.client.authRefresh(body));
        console.warn('Token refreshed successfully!');
        await this.loginUserIfTokenAvailable(authToken);
        resolve();
      } catch (error) {
        const responseMessage: string = error?.result?.error ?? error?.result?.message;

        if (responseMessage) {
          console.error('Token refresh error:', responseMessage);
          await this.toastService.error(responseMessage);
        } else {
          console.error('Token refresh error:', error);
        }
        reject(error);
      } finally {
        this.refreshTokenMutex = null;
      }
    });

    return this.refreshTokenMutex;
  }

  public async banFilter(type: number, callback: Function = null): Promise<void> {
    if (!this.isAuthenticated) {
      if (typeof callback === 'function') await callback();
      return;
    }

    const userData: UserDataResponse = await this.authMe();
    const banShort: BanShortResponse = userData?.data?.bans.find((ban: BanShortResponse) => (ban?.type == type) && (new Date(ban?.date)).getTime() > (new Date()).getTime());

    if (!banShort) {
      if (typeof callback === 'function') await callback();
    } else {
      const untilDate: string = this.datePipe.transform(banShort?.date, 'dd.MM.yyyy hh:mm');
      await this.toastService.error(this.translate.instant(
        (type == 2) ? 'ban.task' : 'ban.reply',
        {date: untilDate, reason: banShort?.reason})
      );
    }
  }

  private async loginUserIfTokenAvailable(authToken: AuthToken): Promise<void> {
    if (!authToken) {
      console.error('No auth token, logout...');
      await this.toastService.warning(this.translate.instant('auth.unauthorized'));
      return await this.logout();
    }

    await this.saveTokens(authToken);
  }

  public async logout(): Promise<void> {
    console.warn('Logout...');
    await this.clearTokens();

    try {
      const withApple: boolean = this.localStorage.getItem('withApple');
      const withGoogle: boolean = this.localStorage.getItem('withGoogle');

      if (withApple || withGoogle) {
        await SocialLogin.logout({provider: (withApple) ? 'apple' : 'google'});
      }
    } catch (error) {
      console.error(error);
    }

    if (typeof window !== 'undefined') {
      location.replace('/login'); // reload page
    }
  }

  private async saveTokens(authToken: AuthToken): Promise<void> {
    try {
      await this.tokenStore.setTokens(authToken?.access_token, authToken?.refresh_token);
      this.parsedToken = ParsedToken.fromToken(authToken?.access_token);
      this.parsedUser = ParsedUser.fromResponse(authToken?.user);
    } catch (error) {
      console.error(error);
    }
  }

  private async clearTokens(): Promise<void> {
    try {
      await this.tokenStore.removeTokens();
      this.parsedToken = null;
      this.parsedUser = null;
    } catch (error) {
      console.error(error);
    }
  }

  // private handleError(): Observable<never> {
  //   return throwError(this.translate.instant('notifications.badData'));
  // }

  // public getUserToken() {
  //   return JSON.parse(localStorage.getItem(this.JWT_TOKEN));
  // }

  // public getUserInfo() {
  //   return JSON.parse(localStorage.getItem(this.USER_INFO));
  // }

  //   public isAdmin() {
  //     const userInfo = this.getUserInfo();
  //     if (userInfo) {
  //       return userInfo?.role.includes('admin');
  //     }
  //     return false;
  //   }

  //   public isUser() {
  //     const userInfo = this.getUserInfo();
  //     if (userInfo) {
  //       return userInfo?.role.includes('user');
  //     }
  //     return false;
  //   }
}

export class ParsedToken {
  userId: number = null;
  expirationDate: Date = null;

  public static fromToken(jwtToken: string): ParsedToken {
    if (!jwtToken) return null;

    const jwtPayload: JwtPayload = jwtDecode(jwtToken);

    const newParsedToken: ParsedToken = new ParsedToken();
    newParsedToken.userId = Number(jwtPayload.sub);
    newParsedToken.expirationDate = new Date(jwtPayload.exp * 1000);

    if (!environment.production) console.info('\x1b[33m' + 'Token expiration date:' + '\x1b[0m', newParsedToken.expirationDate);

    return newParsedToken;
  }
}

export class ParsedUser {
  id: number | null = null;
  name: string | null = null;
  surname: string | null = null;
  nickname: string | null = null;
  phone: string | null = null;
  email: string | null = null;
  avatar: FileResponse | null = null;
  description: string | null = null;
  rate: number | null = null;
  rating: number | null = null;
  skills: SkillsRequest[] | null = null;
  lang: LangResponse | null = null;
  isOpenForProposals: boolean | null = null;
  replyOnResolveDispute: ReplyOnResolveDispute | null = null;
  subscriptionsInfo?: SubscriptionsInfo;
  official: boolean | null = null;

  public static fromResponse(response: UserResponse): ParsedUser {
    if (!response) return null;

    const parsedUser: ParsedUser = new ParsedUser();

    parsedUser.id = response?.id;
    parsedUser.name = response?.name;
    parsedUser.surname = response?.surname;
    parsedUser.nickname = response?.nickname;
    parsedUser.phone = response?.phone;
    parsedUser.email = response?.email;
    parsedUser.avatar = response?.avatar;
    parsedUser.description = response?.description;
    parsedUser.rate = response?.rate;
    parsedUser.rating = response?.rating;
    parsedUser.skills = response?.skills;
    parsedUser.lang = response?.lang;
    parsedUser.isOpenForProposals = response?.is_open_for_proposals;
    parsedUser.replyOnResolveDispute = response?.replyOnResolveDispute;
    parsedUser.subscriptionsInfo = response?.subscriptionsInfo;
    parsedUser.official = response?.official;

    return parsedUser;
  }
}

